import type { IconPattern, ContainerShape, TitleStyle3D } from "../../state/logoStore";
import { buildTitleBlock } from "../../engine/render";
import { buildIconPatternShapes } from "../../engine/patterns";
import { CONTAINER_SHAPES } from "../../engine/containerShapes";

// ============================================================
// 3D text-style picker — live mini-previews using the user's actual font + color
// ============================================================
const TEXT_STYLE_OPTIONS: Array<{ id: TitleStyle3D; label: string }> = [
  { id: "flat",     label: "Flat" },
  { id: "stacked",  label: "Stacked" },
  { id: "chrome",   label: "Chrome" },
  { id: "extruded", label: "Extruded" },
  { id: "gold",     label: "Gold" },
  { id: "neon",     label: "Neon" },
  { id: "outline",  label: "Outline" },
  { id: "glitch",   label: "Glitch" },
  { id: "comic",    label: "Comic" },
];

export function TextStyleThumb({ style, font, color, background }: {
  style: TitleStyle3D; font: string; color: string; background: string;
}) {
  const uid = `thumb-${style}-${Math.random().toString(36).slice(2, 7)}`;
  const { defs, body } = buildTitleBlock({
    text: "Brand",
    anchor: "middle",
    fontFamily: font,
    fontSize: 38,
    color,
    style3D: style,
    depth: 6,
    stroke: 4,
    fitAttr: "",
    uid,
  });
  const bg = style === "neon" ? "#0f172a" : (background === "transparent" || !background ? "#ffffff" : background);
  const html = `<svg viewBox="-90 -32 180 56" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:100%">
    ${defs ? `<defs>${defs}</defs>` : ""}
    <rect x="-90" y="-32" width="180" height="56" fill="${bg}"/>
    ${body}
  </svg>`;
  return <span style={{ display: "block", width: "100%", height: 44, borderRadius: 6, overflow: "hidden" }} dangerouslySetInnerHTML={{ __html: html }} />;
}

export function Style3DPicker({
  selected, font, color, background, onPick,
}: {
  selected: TitleStyle3D;
  font: string;
  color: string;
  background: string;
  onPick: (id: TitleStyle3D) => void;
}) {
  return (
    <div className="text-style-picker">
      <div className="section-label" style={{ marginTop: 12 }}>3D style</div>
      <div className="text-style-grid">
        {TEXT_STYLE_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            className={`text-style-tile ${selected === opt.id ? "active" : ""}`}
            onClick={() => onPick(opt.id)}
            title={opt.label}
          >
            <span className="text-style-preview">
              <TextStyleThumb style={opt.id} font={font} color={color} background={background} />
            </span>
            <span className="text-style-label">{opt.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Pattern catalog — used by both the Symbol pattern and the Container pattern
// ============================================================
export const PATTERN_CATEGORIES: Array<{ heading: string; patterns: Array<{ id: IconPattern; label: string }> }> = [
  {
    heading: "Plain",
    patterns: [
      { id: "none", label: "Solid" },
    ],
  },
  {
    heading: "Stripes",
    patterns: [
      { id: "stripes-h",      label: "Horizontal" },
      { id: "stripes-v",      label: "Vertical" },
      { id: "stripes-diag",   label: "Diagonal" },
      { id: "stripes-thin",   label: "Pinstripes" },
      { id: "stripes-thick",  label: "Bold" },
      { id: "stripes-double", label: "Double-line" },
      { id: "stripes-broken", label: "Broken" },
      { id: "stripes-wavy",   label: "Wavy" },
    ],
  },
  {
    heading: "Dots",
    patterns: [
      { id: "dots",         label: "Grid" },
      { id: "dots-tiny",    label: "Tiny" },
      { id: "dots-large",   label: "Large" },
      { id: "dots-random",  label: "Random" },
      { id: "bubble-dots",  label: "Bubbles" },
      { id: "halftone-fade",label: "Halftone" },
    ],
  },
  {
    heading: "Grids",
    patterns: [
      { id: "grid",              label: "Square" },
      { id: "grid-micro",        label: "Micro" },
      { id: "grid-blueprint",    label: "Blueprint" },
      { id: "checkerboard",      label: "Checker" },
      { id: "checkerboard-tiny", label: "Tiny check" },
    ],
  },
  {
    heading: "Geometric",
    patterns: [
      { id: "honeycomb",    label: "Honeycomb" },
      { id: "crosshatch",   label: "Crosshatch" },
      { id: "carbon-fiber", label: "Carbon" },
    ],
  },
  {
    heading: "Waves",
    patterns: [
      { id: "waves",        label: "Waves" },
      { id: "sound-waves",  label: "Sound" },
      { id: "heartbeat",    label: "Heartbeat" },
      { id: "topographic",  label: "Topographic" },
    ],
  },
  {
    heading: "Specialty",
    patterns: [
      { id: "circuit",          label: "Circuit" },
      { id: "starburst",        label: "Starburst" },
      { id: "concentric-rings", label: "Rings" },
      { id: "bokeh",            label: "Bokeh" },
      { id: "confetti",         label: "Confetti" },
      { id: "speed-lines",      label: "Speed lines" },
    ],
  },
];

export function patternLabel(id: IconPattern): string {
  for (const cat of PATTERN_CATEGORIES) {
    const found = cat.patterns.find((p) => p.id === id);
    if (found) return found.label;
  }
  return id;
}

export function PatternPicker({
  selected,
  onPick,
}: {
  selected: IconPattern;
  onPick: (id: IconPattern) => void;
}) {
  return (
    <div className="pattern-catalog">
      {PATTERN_CATEGORIES.map((cat) => (
        <div key={cat.heading} className="pattern-cat">
          <div className="pattern-cat-heading">{cat.heading}</div>
          <div className="container-grid">
            {cat.patterns.map((p) => (
              <button
                key={p.id}
                className={`container-tile ${selected === p.id ? "active" : ""}`}
                onClick={() => onPick(p.id)}
                title={p.label}
              >
                <span className="container-preview"><PatternThumb pattern={p.id} /></span>
                <span>{p.label}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function PatternThumb({ pattern }: { pattern: IconPattern }) {
  const fg = "#0f172a";
  const overlay = "#ffffff";
  const idSafe = pattern.replace(/[^a-z0-9-]/gi, "");
  const clipId = `pt-clip-${idSafe}`;
  const shapes = pattern === "none"
    ? ""
    : buildIconPatternShapes(pattern, { x: 0, y: 0, w: 24, h: 24 }, overlay);
  const html = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:100%">
    <defs><clipPath id="${clipId}"><circle cx="12" cy="12" r="10"/></clipPath></defs>
    <g clip-path="url(#${clipId})">
      <circle cx="12" cy="12" r="10" fill="${fg}"/>
      ${shapes}
    </g>
  </svg>`;
  return <span style={{ display: "inline-block", width: "100%", height: "100%" }} dangerouslySetInnerHTML={{ __html: html }} />;
}

export function ContainerShapeThumb({ id }: { id: ContainerShape }) {
  if (id === "none") return <span style={{ fontSize: 11, color: "#94a3b8" }}>—</span>;
  if (id === "circle") return <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /></svg>;
  if (id === "rounded") return <svg viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" rx="5" /></svg>;
  if (id === "square") return <svg viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" /></svg>;
  const shape = CONTAINER_SHAPES[id];
  if (!shape) return null;
  const fillRule = shape.fillRule ? ` fill-rule="${shape.fillRule}"` : "";
  const html = `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:100%"><g fill="currentColor"${fillRule}>${shape.body}</g></svg>`;
  return <span style={{ display: "inline-block", width: "100%", height: "100%" }} dangerouslySetInnerHTML={{ __html: html }} />;
}

// ============================================================
// Golden ratio overlay — visual guide on the editor preview
// (φ cross-grid + logarithmic spiral that shrinks by 1/φ per quarter)
// ============================================================
export function GoldenRatioOverlay() {
  const phi = 1.6180339887;
  const size = 500;
  const a = size / phi;
  const b = size - a;
  const cx = size / 2, cy = size / 2;
  const segments: string[] = [];
  let r = size * 0.45;
  let angle = 0;
  const startX = cx + r * Math.cos(angle);
  const startY = cy + r * Math.sin(angle);
  segments.push(`M ${startX.toFixed(2)} ${startY.toFixed(2)}`);
  for (let i = 0; i < 6; i++) {
    angle -= Math.PI / 2;
    const newR = r / phi;
    const ex = cx + newR * Math.cos(angle);
    const ey = cy + newR * Math.sin(angle);
    segments.push(`A ${r.toFixed(2)} ${r.toFixed(2)} 0 0 0 ${ex.toFixed(2)} ${ey.toFixed(2)}`);
    r = newR;
  }
  const spiral = segments.join(" ");
  return (
    <svg className="golden-overlay" viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <g stroke="#d4862a" strokeWidth="1" fill="none" opacity="0.55">
        <line x1={a} y1="0" x2={a} y2={size} />
        <line x1={b} y1="0" x2={b} y2={size} />
        <line x1="0" y1={a} x2={size} y2={a} />
        <line x1="0" y1={b} x2={size} y2={b} />
      </g>
      <path d={spiral} stroke="#d4862a" strokeWidth="1.4" fill="none" opacity="0.85" />
    </svg>
  );
}
